import React, { createContext } from 'react';
import { useAppConfigContext } from './AppConfigProvider';
import { IApiConfig, useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';

export interface IIdpApiResponse {
    results: IIdpEntry[];
    count: number;
}
export interface IIdpEntry {
    id: string;
    displayName: string;
    personAttribute: string;
    brokerType: string;
    type: string;
    brokerData: object;
    /**
     * The specific SAML provider this connection was set up as (e.g. 'OKTA'), if
     * known. Not part of the real API contract today — the Authentication tab's
     * Add wizard deliberately never sends its type choice to the backend — so
     * this is undefined for anything that endpoint doesn't happen to supply.
     * Optional and additive so existing consumers are unaffected.
     */
}

export type IdentityProviderContext = {
    loaded: boolean;
    error: boolean;
    idps: IIdpEntry[] | undefined;
    /** Re-fetch the list, e.g. after a mutation elsewhere (add, delete). */
    refetch: () => void;
};

type IdentityProviderContextInternal = IdentityProviderContext & {
    initFetch: () => void;
};

export const IdentityProviderContext =
    createContext<IdentityProviderContextInternal>({
        loaded: false,
        idps: undefined,
        error: false,
        initFetch: () => {},
        refetch: () => {},
    });

export const useIdentityProviderContext = () => {
    const ctx = React.useContext(IdentityProviderContext);
    // lazy load ctx on 1st use of hook
    React.useEffect(() => {
        ctx.initFetch();
    }, [ctx]);
    return ctx as IdentityProviderContext;
};

export const IdpContextProvider: React.FunctionComponent = ({ children }) => {
    const { api } = useAppConfigContext();
    const user = useUser();
    const initialized = React.useRef(false);
    const [shouldRun, setToRun] = React.useState(false);

    const idpApiConfig = React.useMemo<IApiConfig>(
        () => ({
            /*
             * auth-prov, not bb-identity-provider directly: that service's path
             * takes a tenant ID and nothing else, so an incremental-auth
             * session's locator matched no tenant and came back as an empty
             * list -- the same answer a tenant with no providers gives. This
             * path resolves the locator first and returns the same body.
             */
            url: `${api?.authProv}/v1/data/tenants/${user?.tenantIdentifier}/identityProviders`,
            // Authenticated now, which is what makes that resolution possible;
            // the old endpoint was public and took no token.
            useAuth: true,
            transformResponse: (data: IIdpApiResponse) => data.results,
            executeType: 'manual',
            // A refetch after a mutation (e.g. delete) must reach the network, not
            // a cached response for the same URL — otherwise the list would look
            // refreshed but still show the entry that was just removed.
            useCache: false,
        }),
        [api, user],
    );

    const {
        data: idpApiEntries,
        loading: idpApiLoading,
        error: idpApiError,
        execute: idpApiExecute,
    } = useApi<IIdpEntry[]>(idpApiConfig);

    const init = React.useMemo(
        () => () => {
            setToRun(true);
        },
        [],
    );

    React.useEffect(() => {
        if (initialized.current) {
            return;
        }
        if (user && shouldRun) {
            initialized.current = true;
            idpApiExecute();
        }
    }, [idpApiExecute, user, shouldRun]);

    // Incremental-auth users were forced to the error state here, because the
    // call could not work for them. Through auth-prov it can, so their state
    // follows the response like anyone else's.
    const isError = !!idpApiError;

    return (
        <IdentityProviderContext.Provider
            value={
                {
                    loaded:
                        isError ||
                        ((!idpApiLoading || idpApiLoading !== undefined) &&
                            !idpApiError),
                    error: isError,
                    idps: idpApiEntries,
                    initFetch: init,
                    refetch: idpApiExecute,
                } as IdentityProviderContextInternal
            }
        >
            {children}
        </IdentityProviderContext.Provider>
    );
};

export default IdpContextProvider;
